import config from '../config.js';

/*
 * Product analytics through PostHog (ENG-857).
 *
 * ## Off without a key
 *
 * With `VITE_POSTHOG_PROJECT_TOKEN` unset (local development, tests) nothing here does
 * anything: posthog-js is never even loaded, so no request can reach PostHog.
 * With a key, the SDK is loaded on first use, in its own chunk, so the app
 * never waits on it.
 *
 * ## What is sent
 *
 * - `$pageview` on every route change (`capture_pageview: 'history_change'`),
 *   with the UTM and campaign parameters PostHog saves by default, so email
 *   links can be attributed.
 * - `identify` with the SongShake user id as `distinct_id` once a session is
 *   held, with the person properties from `GET /me` (`personProperties`);
 *   `reset` once it is gone.
 * - `fx_config_rejected` (`captureEvent`, ENG-871) when the FX config or FX
 *   packages from the backend can't be used: `{reason, count}`, where reason
 *   is `invalid_config` or `package_rejected`.
 * - Performer events (ENG-888, `capturePerformerEvent` in `performerEvents.js`),
 *   each sent once its request has succeeded (a failed action sends nothing).
 *   Every one carries `band_id`, plus `show_live` (true | false) and, while a
 *   show is live, `show_id`; both are left out when the show could not be
 *   read. IDs, counts and flags only, never a title, note or other free text:
 *   - `songlist_song_added`: `source` (search | manual | csv_import), with
 *     `song_id` for a single song or `song_count` for an import (one event
 *     per import, and one more for "Import them anyway"). A catalogue add,
 *     "Did you mean" included, is search; "Add it anyway" is manual. Nothing
 *     adds a recommended song to a song list yet, so `recommendation` is
 *     reserved.
 *   - `songlist_song_removed`: `song_id`.
 *   - `song_edited`: `song_id`, `edit` (details | manual | match).
 *   - `go_live_clicked`: when Go Live succeeds; `show_live` and `show_id` as
 *     read after it.
 *   - `end_show_clicked`: when End Show succeeds; `show_id` of the show
 *     ended, `show_live: false`. Automatic ends are the backend's (ENG-858).
 *   - `queue_song_completed`: when the Performer marks the playing entry
 *     played (Finish Song, or Play Next over a playing song): `queued_song_id`,
 *     `was_boosted`, `boost_total_cents` when known, `was_paid_request` and
 *     `position_in_queue`. A song played twice sends two.
 *
 *     The Boost flags come from what the dashboard saw while the entry
 *     waited (`boosted_vibe_id`, `boost_alert` frames), kept per band in
 *     sessionStorage; see `performerEvents.js`.
 *
 * - Upgrade page events (ENG-892, `captureEvent` in `UpgradePage.jsx`), once
 *   per action; roles, intervals and enums only:
 *   - `upgrade_page_viewed`: `current_role`, `preselected_plan` (the
 *     `?plan=` role, or null), once the plans have loaded.
 *   - `upgrade_plan_selected`: a card or hero button opened a confirmation;
 *     `role`, `interval`, `kind` (checkout | upgrade | switch_now |
 *     switch_later | downgrade | cancel), `current_role`.
 *   - `upgrade_checkout_started`: the same properties, as the page leaves
 *     for Stripe Checkout.
 *   - `upgrade_change_confirmed`: the same, plus `effective` (now |
 *     period_end), once the change or cancel succeeded.
 *   - `upgrade_change_failed`: the same, plus `code` (the backend's
 *     error code, or null).
 *   - `upgrade_change_undone`: `role`, `interval` of the plan kept, when
 *     Undo or Keep dropped a scheduled change or a cancel.
 *
 * Nothing else: no autocapture, no session replay (and inputs masked should it
 * ever be turned on), no heatmaps or surveys. A URL keeps only its campaign
 * parameters and loses its hash (`sanitizeUrl`): the OAuth callback carries
 * the sign-in `code` and `state` in its query. The OBS overlay (`/obs/...`)
 * sends nothing; it runs inside the performer's broadcast software.
 */

/** Query parameters a URL keeps on its way to PostHog: campaign attribution only. */
const CAMPAIGN_PARAM = /^(utm_[a-z_]+|gclid|gad_source|fbclid|msclkid|ttclid|li_fat_id|twclid|mc_cid|ref)$/i;

/** Properties holding a URL, whatever the event: `$current_url`, `$initial_referrer`, ... */
const URL_PROPERTY = /(url|referrer)$/i;

export const analyticsEnabled = () => Boolean(config.posthogKey);

/** `url` without its hash and with only its campaign parameters; anything not a URL as is. */
export const sanitizeUrl = (url) => {
  if (typeof url !== 'string' || !/^https?:\/\//i.test(url)) return url;
  try {
    const parsed = new URL(url);
    const kept = new URLSearchParams();
    parsed.searchParams.forEach((value, key) => {
      if (CAMPAIGN_PARAM.test(key)) kept.append(key, value);
    });
    parsed.search = kept.toString();
    parsed.hash = '';
    return parsed.toString();
  } catch {
    return url;
  }
};

const sanitizeProperties = (props) => {
  if (!props || typeof props !== 'object') return props;
  const out = { ...props };
  for (const key of Object.keys(out)) {
    if (URL_PROPERTY.test(key)) out[key] = sanitizeUrl(out[key]);
  }
  return out;
};

const isObsOverlay = (event) => {
  const path = event?.properties?.$pathname ?? window.location.pathname;
  return typeof path === 'string' && path.startsWith('/obs/');
};

/** posthog-js `before_send`: drop the OBS overlay's events, strip every URL. */
export const beforeSend = (event) => {
  if (!event || isObsOverlay(event)) return null;
  return {
    ...event,
    properties: sanitizeProperties(event.properties),
    ...(event.$set && { $set: sanitizeProperties(event.$set) }),
    ...(event.$set_once && { $set_once: sanitizeProperties(event.$set_once) }),
  };
};

export const posthogOptions = () => ({
  api_host: config.posthogHost,
  person_profiles: 'identified_only',
  capture_pageview: 'history_change',
  capture_pageleave: 'if_capture_pageview',
  save_campaign_params: true,
  autocapture: false,
  capture_dead_clicks: false,
  capture_heatmaps: false,
  capture_exceptions: false,
  rageclick: false,
  disable_session_recording: true,
  session_recording: { maskAllInputs: true },
  mask_all_text: true,
  mask_all_element_attributes: true,
  disable_surveys: true,
  disable_product_tours: true,
  disable_conversations: true,
  disable_web_experiments: true,
  advanced_disable_feature_flags: true,
  before_send: beforeSend,
});

/**
 * The person properties for a `GET /me` body (ENG-724):
 *
 * - `engagement_tier`: as `/me` names it (Listener, Listener Premium,
 *   Performer, Performer Premium), with `role` (listener | performer) and
 *   `premium` split out of it.
 * - `band_ids`: the bands they are a member or moderator of.
 * - `is_internal`: once `/me` carries it (ENG-856); left out until then, so a
 *   missing field never marks anyone external.
 * - `frontend_version`: once the deploy stamps one (ENG-848).
 */
export const personProperties = (me) => {
  const props = {};
  const tier = typeof me?.engagementTier === 'string' ? me.engagementTier : '';
  if (tier) {
    props.engagement_tier = tier;
    props.role = /^performer/i.test(tier) ? 'performer' : 'listener';
    props.premium = /premium/i.test(tier);
  }
  if (Array.isArray(me?.bands)) {
    props.band_ids = me.bands.map((band) => band?.id).filter((id) => id != null);
  }
  const internal = me?.isInternal ?? me?.is_internal;
  if (typeof internal === 'boolean') props.is_internal = internal;
  if (config.appVersion) props.frontend_version = config.appVersion;
  return props;
};

let clientPromise = null;

/** The initialised posthog-js client, loaded once; null with analytics off or a failed load. */
export const loadAnalytics = () => {
  if (!analyticsEnabled()) return Promise.resolve(null);
  if (!clientPromise) {
    clientPromise = import('posthog-js')
      .then(({ default: posthog }) => {
        posthog.init(config.posthogKey, posthogOptions());
        return posthog;
      })
      .catch((err) => {
        console.error('Failed to load analytics:', err);
        return null;
      });
  }
  return clientPromise;
};

/** Ties this browser's events to the signed-in account. */
export const identifyUser = async (me) => {
  if (me?.id == null) return;
  const posthog = await loadAnalytics();
  posthog?.identify(String(me.id), personProperties(me));
};

/**
 * Signed out: later events start a new anonymous person. Only when this
 * browser is still identified, which PostHog remembers across page loads (a
 * sign-out that reloads the page, a session that ended with the tab).
 */
export const resetAnalytics = async () => {
  const posthog = await loadAnalytics();
  if (posthog?.get_property?.('$user_state') === 'identified') posthog.reset();
};

/**
 * Sends one app event with these properties. Fire and forget: with analytics
 * off it does nothing, and it never throws.
 */
export const captureEvent = async (name, properties = {}) => {
  try {
    const posthog = await loadAnalytics();
    posthog?.capture(name, properties);
  } catch (_e) {
    // Analytics must never break the page.
  }
};

/** For tests: forget the loaded client. */
export const _resetForTests = () => {
  clientPromise = null;
};
